import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Plus } from "lucide-react";
import SEO from "../components/SEO";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import StatusBadge from "../components/StatusBadge";
import { FAQS, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const FAQ_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

function FaqItem({ f, i, open, onToggle }) {
    return (
        <FadeUp delay={Math.min(i, 5) * 0.05}>
            <div className={`border transition-colors duration-300 ${open ? "border-midnight bg-white" : "border-midnight/10 bg-white hover:border-midnight/40"}`}>
                <button
                    data-testid={`faq-question-${i}`}
                    onClick={onToggle}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between gap-6 px-6 py-6 text-left md:px-8"
                >
                    <span className="flex items-baseline gap-4">
                        <span className="hidden font-display text-lg font-semibold text-midnight/25 sm:block">
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display text-lg font-semibold tracking-tight md:text-xl">{f.q}</span>
                    </span>
                    <motion.span
                        animate={{ rotate: open ? 45 : 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className={`flex h-9 w-9 shrink-0 items-center justify-center ${open ? "bg-emergency text-white" : "bg-midnight/5 text-midnight"}`}
                    >
                        <Plus className="h-5 w-5" />
                    </motion.span>
                </button>
                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                        >
                            <p data-testid={`faq-answer-${i}`} className="px-6 pb-7 leading-relaxed text-midnight/70 sm:pl-[4.25rem] md:px-8 md:pl-[4.75rem]">
                                {f.a}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </FadeUp>
    );
}

export default function Faq() {
    const [open, setOpen] = useState(0);

    return (
        <div data-testid="faq-page">
            <SEO
                title="FAQ Serrurier Paris — Prix, Délais, Devis | Réponses d'Artisans"
                description="FAQ serrurier Paris : toutes les réponses sur nos dépannages, prix ouverture de porte 60€ TTC, délai 30 min, devis gratuit, agrément assurance, zones couvertes. ☎ 06 25 25 86 19"
                path="/faq"
                schema={FAQ_SCHEMA}
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">FAQ</span>
                    </nav>
                    <SectionLabel dark>Foire aux questions</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Vos questions,", "nos réponses d'artisans."]} />
                    </h1>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
                <div className="space-y-4" data-testid="faq-accordion">
                    {FAQS.map((f, i) => (
                        <FaqItem key={f.q} f={f} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
                    ))}
                </div>

                <FadeUp delay={0.1}>
                    <div className="grain mt-16 flex flex-wrap items-center justify-between gap-6 bg-midnight p-8 text-white md:p-10" data-testid="faq-cta">
                        <div>
                            <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">
                                Une autre question ? Appelez-nous.
                            </h2>
                            <p className="mt-2 text-sm text-white/60">Conseil gratuit, 24h/24 — un artisan vous répond.</p>
                        </div>
                        <a
                            href={PHONE_TEL}
                            data-testid="faq-call-button"
                            className="flex items-center gap-3 bg-emergency px-7 py-4 font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                    </div>
                </FadeUp>
            </section>
        </div>
    );
}
